/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { type FunctionComponent } from 'react'
import { useParams } from 'react-router-dom'

import { FormattedMessage } from '@modules/i18n'
import useGetBudget from '@modules/billing-lib/queries/useGetBudget'
import useUpdateBudget, {
  type UpdateBudgetBody,
} from '@modules/billing-lib/mutations/useUpdateBudget'
import Feature from '@modules/utils/feature'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import { isFeatureActivated } from '@/store'

import BudgetSettings from '../components/BudgetSettings'
import BudgetSettingsR1 from '../components/BudgetSettingsR1'
import useBillingNotificationsNav from '../../hooks/useBillingNotificationsNav'

import LoadingError from './LoadingError'

import type { BudgetConfigurationValues } from '../types'

const EditBudget: FunctionComponent = () => {
  const { budgetId } = useParams<{ budgetId?: string }>()
  const organizationId = useOrganizationId()

  const {
    data: budgetData,
    isLoading: isLoadingBudget,
    isError: isLoadingBudgetError,
  } = useGetBudget(organizationId, budgetId)
  const { goToBillingNotifications } = useBillingNotificationsNav()
  const updateBudgetMutation = useUpdateBudget()

  const { name, amount = 0, scope_type: scopeType, scope_values: scopeValues } = budgetData || {}
  const initialValues: BudgetConfigurationValues = {
    name,
    amount,
    scopeType,
    scopeValues,
  }

  const isBillingCostAlertsCloudResourceFeatureFlagEnabled = isFeatureActivated(
    Feature.billingCostAlertsCloudResource,
  )

  const onSaveBudget = (values: BudgetConfigurationValues) => {
    if (!organizationId || !budgetData || !budgetId) {
      return
    }

    // TODO: remove once edit budget R1 is deprecated
    const updatedBudget: UpdateBudgetBody = isBillingCostAlertsCloudResourceFeatureFlagEnabled
      ? {
          active: budgetData.active,
          alerts: budgetData.alerts.map(
            ({ operator, threshold, threshold_type }): UpdateBudgetBody['alerts'][number] => ({
              operator,
              threshold,
              threshold_type,
            }),
          ),
          amount: values.amount,
          name: values.name,
          period: budgetData.period,
          scope_type: budgetData.scope_type,
          scope_values: budgetData.scope_values,
        }
      : {
          active: budgetData.active,
          alerts: budgetData.alerts.map(
            ({ operator, threshold, threshold_type }): UpdateBudgetBody['alerts'][number] => ({
              operator,
              threshold,
              threshold_type,
            }),
          ),
          period: budgetData.period,
          scope_type: budgetData.scope_type,
          scope_values: budgetData.scope_values,
          ...values,
        }

    updateBudgetMutation.mutate(
      {
        budgetId,
        organizationId,
        body: updatedBudget,
      },
      {
        onSuccess: goToBillingNotifications,
      },
    )
  }

  if (isLoadingBudgetError) {
    return <LoadingError />
  }

  const isSubmitting = updateBudgetMutation.isLoading
  const alerts = budgetData?.alerts || []

  return isBillingCostAlertsCloudResourceFeatureFlagEnabled ? (
    <BudgetSettings
      alerts={alerts}
      data-testid='billing.notifications.edit-budget.budget-settings'
      initialValues={initialValues}
      isLoading={isLoadingBudget}
      isSubmitting={isSubmitting}
      isMutationError={updateBudgetMutation.isError}
      onSubmit={onSaveBudget}
      title={
        <FormattedMessage
          id='billing.notifications.budgets.edit-budget.title'
          defaultMessage='Edit Budget'
        />
      }
    />
  ) : (
    <BudgetSettingsR1
      alerts={alerts}
      data-testid='billing.notifications.edit-budget.budget-settings-r1'
      initialValues={initialValues}
      isLoading={isLoadingBudget}
      isSubmitting={isSubmitting}
      isMutationError={updateBudgetMutation.isError}
      onSubmit={onSaveBudget}
      title={
        <FormattedMessage
          id='billing.notifications.budgets.edit-budget.title'
          defaultMessage='Edit Budget'
        />
      }
    />
  )
}

export default EditBudget
